import React, { useEffect, useRef, useState } from "react";
import { PasswordInput, PinInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocation, useNavigate } from "react-router-dom";
import ApiService from "../../services/api";
import fireToast from "../../helpers/toaster";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import { PATHS } from "../../constants/paths";
import styles from "./Login.module.scss";

const RESEND_COOLDOWN_SECONDS = 60;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const getStep = (pathname) => {
  if (pathname === PATHS.FORGOT_PASSWORD_VERIFY) return "verify";
  if (pathname === PATHS.FORGOT_PASSWORD_NEW_PASSWORD) return "password";
  return "email";
};

const getErrorDetails = (error) => {
  const data = error?.response?.data;
  const bodyRetry = Number(data?.retryAfterSeconds);
  const headerRetry = Number(error?.response?.headers?.["retry-after"]);
  let retryAfterSeconds = 0;

  if (Number.isFinite(bodyRetry) && bodyRetry > 0) {
    retryAfterSeconds = bodyRetry;
  } else if (Number.isFinite(headerRetry) && headerRetry > 0) {
    retryAfterSeconds = headerRetry;
  }

  const message =
    (typeof data?.message === "string" && data.message) ||
    "Something went wrong. Please try again.";

  return {
    status: error?.response?.status,
    message,
    retryAfterSeconds,
  };
};

const formatClock = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const step = getStep(location.pathname);
  const email = (location.state?.email || "").trim();
  const resetToken = location.state?.resetToken || "";
  const expiresAt = location.state?.expiresAt || 0;

  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [now, setNow] = useState(Date.now());
  const [resendUntil, setResendUntil] = useState(
    () => location.state?.resendAvailableAt || 0,
  );
  const [verifyLockedUntil, setVerifyLockedUntil] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const expiredRef = useRef(false);

  const emailForm = useForm({
    initialValues: {
      email,
    },
    validate: {
      email: (value) =>
        EMAIL_PATTERN.test(value.trim()) ? null : "Invalid email",
    },
  });

  const passwordForm = useForm({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      newPassword: (value) =>
        value.length >= 6 ? null : "Password must be at least 6 characters",
      confirmPassword: (value, values) =>
        value === values.newPassword ? null : "Passwords do not match",
    },
  });

  const resendSeconds = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const verifyLockSeconds = Math.max(
    0,
    Math.ceil((verifyLockedUntil - now) / 1000),
  );

  useEffect(() => {
    if (location.state?.resendAvailableAt) {
      setResendUntil(location.state.resendAvailableAt);
    }
  }, [location.state?.resendAvailableAt]);

  useEffect(() => {
    if (step !== "email") return;
    emailForm.setFieldValue("email", email);
    // setFieldValue is recreated when inline validation rules change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, email]);

  useEffect(() => {
    if (step === "verify" && !email) {
      navigate(PATHS.FORGOT_PASSWORD, { replace: true });
    }
    if (step === "password" && !resetToken) {
      navigate(PATHS.FORGOT_PASSWORD, {
        replace: true,
        state: email ? { email } : undefined,
      });
    }
  }, [step, email, resetToken, navigate]);

  useEffect(() => {
    if (step === "password") return undefined;
    if (resendUntil <= Date.now() && verifyLockedUntil <= Date.now()) {
      return undefined;
    }

    const id = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= resendUntil && current >= verifyLockedUntil) {
        window.clearInterval(id);
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [step, resendUntil, verifyLockedUntil]);

  useEffect(() => {
    if (step !== "password" || !resetToken || !expiresAt) return undefined;
    expiredRef.current = false;

    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        fireToast(
          "error",
          "Invalid or expired reset token",
          "bottom-right",
          true,
        );
        navigate(PATHS.FORGOT_PASSWORD, {
          replace: true,
          state: email ? { email } : undefined,
        });
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [step, resetToken, expiresAt, email, navigate]);

  const handleRequest = (values) => {
    const trimmedEmail = values.email.trim();
    setLoading(true);

    ApiService.requestPasswordReset(trimmedEmail)
      .then(() => {
        navigate(PATHS.FORGOT_PASSWORD_VERIFY, {
          state: {
            email: trimmedEmail,
            resendAvailableAt: Date.now() + RESEND_COOLDOWN_SECONDS * 1000,
          },
        });
      })
      .catch((error) => {
        const { message, retryAfterSeconds } = getErrorDetails(error);
        fireToast("error", message, "bottom-right", true);
        if (retryAfterSeconds > 0) {
          const current = Date.now();
          setNow(current);
          setResendUntil(current + retryAfterSeconds * 1000);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleResend = () => {
    if (!email || loading || resendSeconds > 0) return;
    setLoading(true);

    ApiService.requestPasswordReset(email)
      .then(() => {
        const current = Date.now();
        const resendAvailableAt = current + RESEND_COOLDOWN_SECONDS * 1000;
        setNow(current);
        setResendUntil(resendAvailableAt);
        setOtp("");
        navigate(PATHS.FORGOT_PASSWORD_VERIFY, {
          replace: true,
          state: { email, resendAvailableAt },
        });
      })
      .catch((error) => {
        const { message, retryAfterSeconds } = getErrorDetails(error);
        fireToast("error", message, "bottom-right", true);
        if (retryAfterSeconds > 0) {
          const current = Date.now();
          setNow(current);
          setResendUntil(current + retryAfterSeconds * 1000);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleVerify = (event) => {
    event.preventDefault();
    if (!email || otp.length !== 6 || loading || verifyLockSeconds > 0) return;
    setLoading(true);

    ApiService.verifyPasswordReset({ email, otp })
      .then((response) => {
        const token = response?.data?.resetToken;
        const expiresInSeconds =
          Number(response?.data?.expiresInSeconds) || 600;
        if (!token) {
          fireToast("error", "Invalid or expired code", "bottom-right", true);
          return;
        }
        navigate(PATHS.FORGOT_PASSWORD_NEW_PASSWORD, {
          state: {
            email,
            resetToken: token,
            expiresAt: Date.now() + expiresInSeconds * 1000,
          },
        });
      })
      .catch((error) => {
        const { status, message, retryAfterSeconds } = getErrorDetails(error);
        if (status === 429) {
          fireToast(
            "error",
            message || "Too many incorrect codes. Request a new code.",
            "bottom-right",
            true,
          );
          setOtp("");
          if (retryAfterSeconds > 0) {
            const current = Date.now();
            setNow(current);
            setVerifyLockedUntil(current + retryAfterSeconds * 1000);
          }
          return;
        }
        fireToast(
          "error",
          status === 400 ? message || "Invalid or expired code" : message,
          "bottom-right",
          true,
        );
      })
      .finally(() => setLoading(false));
  };

  const handleConfirm = (values) => {
    if (!resetToken || loading) return;
    setLoading(true);

    ApiService.confirmPasswordReset({
      resetToken,
      newPassword: values.newPassword,
    })
      .then((response) => {
        const message =
          response?.data?.message ||
          "Password updated successfully. Please sign in again.";
        fireToast("success", message, "bottom-right", true);
        navigate(PATHS.LOGIN, { replace: true });
      })
      .catch((error) => {
        const { message } = getErrorDetails(error);
        fireToast("error", message, "bottom-right", true);
        if (/reset token/i.test(message) && /invalid|expired/i.test(message)) {
          navigate(PATHS.FORGOT_PASSWORD, {
            replace: true,
            state: email ? { email } : undefined,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {step === "email" && (
        <>
          <div className={styles.heading_container}>
            <button
              type="button"
              className={styles.back_link}
              onClick={() => navigate(PATHS.LOGIN)}
            >
              Back to sign in
            </button>
            <p className={styles.heading}>Forgot password</p>
            <p className={styles.text}>Enter the email you use to sign in</p>
          </div>

          <form onSubmit={emailForm.onSubmit(handleRequest)}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              size="md"
              required
              {...emailForm.getInputProps("email")}
            />
            <PrimaryButton
              text={
                resendSeconds > 0
                  ? `Send code in ${resendSeconds}s`
                  : "Send code"
              }
              type="submit"
              loading={loading}
              disabled={resendSeconds > 0}
            />
          </form>
        </>
      )}

      {step === "verify" && (
        <>
          <div className={styles.heading_container}>
            <button
              type="button"
              className={styles.back_link}
              onClick={() =>
                navigate(PATHS.FORGOT_PASSWORD, { state: { email } })
              }
            >
              Change email
            </button>
            <p className={styles.heading}>Enter code</p>
            <p className={styles.text}>
              If an account exists for this email, a reset code has been sent.
            </p>
            {email ? <p className={styles.email_line}>{email}</p> : null}
          </div>

          <form onSubmit={handleVerify}>
            <div className={styles.otp_field}>
              <span className={styles.otp_label}>Code</span>
              <PinInput
                length={6}
                type="number"
                oneTimeCode
                autoFocus
                size="md"
                value={otp}
                onChange={setOtp}
                disabled={verifyLockSeconds > 0}
                aria-label="6-digit code"
              />
            </div>
            {verifyLockSeconds > 0 ? (
              <p className={styles.helper}>
                Try again in {verifyLockSeconds}s.
              </p>
            ) : null}
            <div className={styles.resend_row}>
              <button
                type="button"
                className={styles.resend_button}
                onClick={handleResend}
                disabled={loading || resendSeconds > 0}
              >
                {resendSeconds > 0
                  ? `Resend code in ${resendSeconds}s`
                  : "Resend code"}
              </button>
            </div>
            <PrimaryButton
              text="Verify code"
              type="submit"
              loading={loading}
              disabled={otp.length !== 6 || verifyLockSeconds > 0}
            />
          </form>
        </>
      )}

      {step === "password" && (
        <>
          <div className={styles.heading_container}>
            <button
              type="button"
              className={styles.back_link}
              onClick={() =>
                navigate(PATHS.FORGOT_PASSWORD, {
                  replace: true,
                  state: email ? { email } : undefined,
                })
              }
            >
              Start over
            </button>
            <p className={styles.heading}>New password</p>
            <p className={styles.text}>
              Choose a new password for your account
            </p>
            {remainingSeconds > 0 ? (
              <p className={styles.helper}>
                This reset expires in {formatClock(remainingSeconds)}.
              </p>
            ) : null}
          </div>

          <form onSubmit={passwordForm.onSubmit(handleConfirm)}>
            <PasswordInput
              label="New password"
              placeholder="Enter new password"
              size="md"
              required
              {...passwordForm.getInputProps("newPassword")}
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Re-enter new password"
              size="md"
              required
              {...passwordForm.getInputProps("confirmPassword")}
            />
            <PrimaryButton
              text="Update password"
              type="submit"
              loading={loading}
            />
          </form>
        </>
      )}
    </>
  );
};

export default ForgotPassword;
